import apiApp from '@/api/modules/app'
import router from '@/router'
import eventBus from '@/utils/eventBus'

interface accountItem {
  token: string
  account: string
  avatar: string
}

interface profileItem {
  id: number
  username: string
  nickname?: string
  avatar?: string
  email?: string
  phone?: string
  realname?: string
  position?: string
  gender?: number
  department_id?: number
  is_super_admin?: boolean
  last_login_ip?: string
  last_login_time?: string
  login_count?: number
}

export const useAppAccountStore = defineStore(
  'appAccount',
  () => {
    const appSettingsStore = useAppSettingsStore()
    const appTabbarStore = useAppTabbarStore()
    const appRouteStore = useAppRouteStore()
    const appMenuStore = useAppMenuStore()
    const dictionaryStore = useDictionaryStore()

    const token = ref(localStorage.getItem('token') ?? '')
    const account = ref(localStorage.getItem('account') ?? '')
    const avatar = ref(localStorage.getItem('avatar') ?? '')
    const profile = ref<profileItem | null>(null)
    const permissions = ref<string[]>([])
    const multipleAccounts = ref<accountItem[]>([])

    const isLogin = computed(() => !!token.value)

    async function login(data: {
      account: string
      password: string
    }) {
      const res = await apiApp.login({
        username: data.account,
        password: data.password,
      })
      const user = res.data.user ?? {}
      // account 作为当前登录主体的稳定标识，统一使用 username，避免昵称变化后影响缓存键和二次登录。
      const nextAccount = user.username || data.account
      const nextAvatar = user.avatar || ''
      const nextToken = res.data.access_token || ''

      localStorage.setItem('account', nextAccount)
      localStorage.setItem('token', nextToken)
      localStorage.setItem('avatar', nextAvatar)
      if (res.data.refresh_token) {
        localStorage.setItem('refresh_token', res.data.refresh_token)
      }

      account.value = nextAccount
      token.value = nextToken
      avatar.value = nextAvatar
      profile.value = user
      permissions.value = []

      if (appSettingsStore.settings.app.account.multipleAccounts) {
        multipleAccountsAdd({
          account: nextAccount,
          token: nextToken,
          avatar: nextAvatar,
        })
      }
    }

    function logout(redirect = router.currentRoute.value.fullPath) {
      void apiApp.logout().catch(() => {})
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      token.value = ''
      router.push({
        name: 'login',
        query: {
          ...(redirect !== appSettingsStore.settings.app.home.fullPath && router.currentRoute.value.name !== 'login' && { redirect }),
        },
      }).then(logoutCleanStatus)
    }

    function requestLogout() {
      if (appSettingsStore.settings.app.account.expiredMode === 'redirect' || !appRouteStore.isGenerate) {
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        token.value = ''
        router.push({
          name: 'login',
          query: {
            ...(
              router.currentRoute.value.fullPath !== appSettingsStore.settings.app.home.fullPath
              && router.currentRoute.value.name !== 'login'
              && {
                redirect: router.currentRoute.value.fullPath,
              }
            ),
          },
        }).then(logoutCleanStatus)
      }
      else {
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        eventBus.emit('global-login-again-visible')
      }
    }

    function logoutCleanStatus() {
      const logoutAccount = account.value
      localStorage.removeItem('account')
      localStorage.removeItem('avatar')
      account.value = ''
      avatar.value = ''
      profile.value = null
      permissions.value = []
      appSettingsStore.updateSettings({}, true)
      appTabbarStore.clean()
      appRouteStore.removeRoutes()
      appMenuStore.setActived(0)
      dictionaryStore.clear()
      if (logoutAccount) {
        multipleAccounts.value = multipleAccounts.value.filter(item => item.account !== logoutAccount)
      }
    }

    async function getPermissions() {
      // 资料和权限拆开读取，供路由与应用启动阶段统一消费。
      const [profileRes, permissionRes] = await Promise.all([
        apiApp.profile(),
        apiApp.permission(),
      ])
      const profilePayload = profileRes.data
      profile.value = profilePayload.user
      permissions.value = permissionRes.data.permissions ?? []
      // 这里继续保持 account 为稳定用户名，避免偏好、标签页、多账号切换等本地状态被昵称影响。
      account.value = profilePayload.user.username || account.value
      avatar.value = profilePayload.user.avatar || ''
      localStorage.setItem('account', account.value)
      localStorage.setItem('avatar', avatar.value)
    }

    async function editProfile(data: {
      nickname: string
      realname: string
      phone: string
      email: string
      avatar: string
      gender: number
      position: string
    }) {
      const res = await apiApp.profileEdit(data)
      profile.value = res.data.user
      account.value = res.data.user.username || account.value
      avatar.value = res.data.user.avatar || ''
      localStorage.setItem('account', account.value)
      localStorage.setItem('avatar', avatar.value)
    }

    async function editPassword(data: {
      old_password: string
      new_password: string
      confirm_password: string
    }) {
      await apiApp.passwordEdit(data)
    }

    async function multipleAccountsCheck(data: {
      account: string
      password: string
    }) {
      const res = await apiApp.login({
        username: data.account,
        password: data.password,
      })
      const user = res.data.user ?? {}
      multipleAccountsAdd({
        token: res.data.access_token,
        account: user.username || data.account,
        avatar: user.avatar || '',
      })
    }

    function multipleAccountsAdd(accItem: accountItem) {
      const index = multipleAccounts.value.findIndex(item => item.account === accItem.account)
      if (index !== -1) {
        multipleAccounts.value[index] = accItem
      }
      else {
        multipleAccounts.value.push(accItem)
      }
    }

    function multipleAccountsRemove(acc?: accountItem['account']) {
      if (acc) {
        multipleAccounts.value = multipleAccounts.value.filter(item => item.account !== acc)
      }
      else {
        multipleAccounts.value = []
      }
    }

    function multipleAccountsSwitch(acc: string) {
      const item = multipleAccounts.value.find(item => item.account === acc)
      if (item) {
        localStorage.setItem('account', item.account)
        localStorage.setItem('token', item.token)
        localStorage.setItem('avatar', item.avatar)
        window.location.reload()
      }
    }

    function lock() {
      localStorage.removeItem('token')
    }

    function unlock() {
      localStorage.setItem('token', token.value)
    }

    return {
      token,
      account,
      avatar,
      profile,
      permissions,
      multipleAccounts,
      isLogin,
      login,
      logout,
      requestLogout,
      getPermissions,
      editProfile,
      editPassword,
      multipleAccountsCheck,
      multipleAccountsAdd,
      multipleAccountsRemove,
      multipleAccountsSwitch,
      lock,
      unlock,
    }
  },
  {
    persist: {
      pick: ['multipleAccounts'],
    },
  },
)
